import { Input, Modal, message } from '@duxton-web/ui';
import i18n from 'i18n-js';
import _ from 'lodash';
import {
  contentConfigGenerateMystiqueV2,
  cpApprove, cpCreateProduct, cpGetCountries, cpGetPersonas, cpGetVerticals, cpListAttributes,
  cpListAudiences, cpListCvps, cpListProducts, cpRecall, cpReject, cpUpdateProduct,
} from 'omega-rtc/actions/ucd';
import type {
  ProductAudienceAssociation,
  ProductAudienceOption,
} from 'omega-rtc/types/ts/content-config';
import {
  isHolosPortalFeatureEnabled,
  useHolosPortalFeatureFlag,
} from 'omega-rtc/utils/portal-config';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import {
  STATUS_PENDING, audienceDisplayLabel, audienceReferenceFromApi,
  buildProductAudienceOptions, countryFromApi, cvpFromApi, formatExpiry,
  isActive, isAudienceReasonValid, localeSorter, personaFromApi,
  productAttributeOptionsFromApi, productDisplayName, productFromApi,
  productToApiBody, sameAudienceAssociations,
  verticalFromApi,
} from './adapters';
import {
  NewProductModal, ProductOfferingDetail, ProductOfferingList,
} from './ProductOfferingView';
import ContentGenerationModal from './ContentGenerationModal';
import ContentGenerationSetupModal from './ContentGenerationSetupModal';
import {
  buildContentConfigGenerationRequests,
  selectMissingContentConfigGenerationRequests,
} from './ConfigContentGeneration.helpers';
import {
  EMPTY_CONTENT_CONFIG_PERMISSIONS,
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitProductForApproval,
} from './permissions';
import useContentConfigWrite from './useContentConfigWrite';

const DRAFT_KEY = '__draft__';
const FORBIDDEN_MESSAGE = "You don't have permission to perform this action.";
const EMPTY_ARRAY: any[] = [];
const EMPTY_STRING_ARRAY: string[] = [];
const EMPTY_GENERATION_REQUESTS: any[] = [];
const EMPTY_OBJECT = {};
const EMPTY_GENERATION_RUN = {
  attributes: EMPTY_STRING_ARRAY,
  attributeDefinitions: {},
  context: {
    country: '', endDate: '', language: '', product: '', startDate: '', vertical: '',
  },
  productUUID: '',
  requests: EMPTY_GENERATION_REQUESTS,
};

export const isProductOfferingAudiencesEnabled = () =>
  isHolosPortalFeatureEnabled('enabledProductOfferingAudiences');

export const getProductAudienceValidationState = (
  audiencesEnabled: boolean,
  audienceListLoaded: boolean,
  associations: ProductAudienceAssociation[],
  optionByKey: Map<string, ProductAudienceOption>,
) => {
  const hasAssociations = audiencesEnabled && associations.length > 0;
  const audienceValidationUnavailable = hasAssociations && !audienceListLoaded;
  const hasInvalidReference = hasAssociations
    && audienceListLoaded
    && associations.some(({ audienceKey }) => {
      const option = optionByKey.get(audienceKey);
      return !option || !option.eligible;
    });

  return {
    audienceValidationUnavailable,
    hasInvalidAudiences: audienceValidationUnavailable || hasInvalidReference,
  };
};

const ProductOfferingPage = ({
  contentConfigPermissions = EMPTY_CONTENT_CONFIG_PERMISSIONS,
  navTick,
}) => {
  const { canCreate, canUpdate, canReview } = contentConfigPermissions;
  const dispatch = useDispatch();
  const userEmail = useSelector((s) => _.get(s, 'app.user.email', ''));
  const contentConfigAudiencesEnabled = useHolosPortalFeatureFlag(
    'enabledContentConfigAudiences',
  );
  const productOfferingAudiencesEnabled = useHolosPortalFeatureFlag(
    'enabledProductOfferingAudiences',
  );

  const [country, setCountry] = useState('');
  // Deep-link params (?country=SG&key=...) captured once on mount so a shared
  // URL opens the config directly. Consumed once the data loads; cleared on
  // any manual navigation so a stale link can't hijack the view later.
  const [deepLink, setDeepLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { country: params.get('country') || '', key: params.get('key') || '' };
  });
  const clearDeepLink = useCallback(() => setDeepLink({ country: '', key: '' }), []);

  // ---- reference + product data from the store (memoised so the adapter maps
  // only re-run when the selected slice actually changes, not every render) ----
  const cpVerticals = useSelector((s) => _.get(s, 'ucd.cpVerticals', EMPTY_ARRAY));
  const cpCvps = useSelector((s) => _.get(s, 'ucd.cpCvps', EMPTY_ARRAY));
  const cpAttributes = useSelector((s) => _.get(s, 'ucd.cpAttributes', EMPTY_ARRAY));
  const countryActiveAudiencesRaw = useSelector((s) =>
    _.get(s, ['ucd', 'cpAudiencesByScope', country, 'active'], EMPTY_ARRAY));
  const cpPersonas = useSelector((s) => _.get(s, 'ucd.cpPersonas', EMPTY_ARRAY));
  const cpCountries = useSelector((s) => _.get(s, 'ucd.cpCountries', EMPTY_ARRAY));
  const allActiveAudiencesRaw = useSelector((s) =>
    _.get(s, ['ucd', 'cpAudiencesByScope', '__all__', 'active'], EMPTY_ARRAY));
  const activeCvpsRaw = useSelector((s) => _.get(s, 'ucd.cpCvpsByCountry.__all__.active', EMPTY_ARRAY));
  const verticals = useMemo(() => cpVerticals.map(verticalFromApi), [cpVerticals]);
  const cvpOptions = useMemo(
    () => (activeCvpsRaw.length ? activeCvpsRaw : cpCvps).map(cvpFromApi).filter((c) => isActive(c.status)),
    [activeCvpsRaw, cpCvps],
  );
  const attributeAudiencesRaw = useMemo(
    () => (productOfferingAudiencesEnabled
      ? allActiveAudiencesRaw.filter((config) => {
        const audienceCountry = _.get(config, 'meta.country', '');
        return !audienceCountry || audienceCountry === country;
      })
      : countryActiveAudiencesRaw),
    [
      allActiveAudiencesRaw,
      country,
      countryActiveAudiencesRaw,
      productOfferingAudiencesEnabled,
    ],
  );
  const attrOptions = useMemo(() => productAttributeOptionsFromApi({
    attributes: cpAttributes,
    audiences: attributeAudiencesRaw,
    audiencesEnabled: contentConfigAudiencesEnabled,
  }), [attributeAudiencesRaw, contentConfigAudiencesEnabled, cpAttributes]);
  const personaOptions = useMemo(() => cpPersonas.map(personaFromApi), [cpPersonas]);
  const countries = useMemo(
    () => cpCountries.map(countryFromApi).sort(localeSorter('name')),
    [cpCountries],
  );
  const audienceReferences = useMemo(
    () => allActiveAudiencesRaw.map(audienceReferenceFromApi),
    [allActiveAudiencesRaw],
  );
  const activeRaw = useSelector((s) => _.get(s, `ucd.cpProductsByCountry.${country}.active`, EMPTY_ARRAY));
  const pendingRaw = useSelector((s) => _.get(s, `ucd.cpProductsByCountry.${country}.pending`, EMPTY_ARRAY));
  const activeListReq = useSelector((s) => _.get(s, 'requests.cpListProductsRequest', EMPTY_OBJECT));
  const pendingListReq = useSelector((s) => _.get(s, 'requests.cpListProductsPendingRequest', EMPTY_OBJECT));
  const audienceListReq = useSelector((s) =>
    _.get(s, 'requests.cpListAudiencesRequest', EMPTY_OBJECT));
  const generationRequest = useSelector((s) =>
    _.get(s, 'requests.contentConfigGenerateMystiqueV2Request', EMPTY_OBJECT));
  const generationResponses = useSelector((s) =>
    _.get(s, 'ucd.contentConfigGenerationByAttribute', EMPTY_OBJECT));
  const generationReadyAttributes = useSelector((s) =>
    _.get(s, 'ucd.contentConfigGenerationReadyAttributes', EMPTY_ARRAY));
  const generationFailedAttributes = useSelector((s) =>
    _.get(s, 'ucd.contentConfigGenerationFailedAttributes', EMPTY_ARRAY));
  // Active and pending lists are fetched separately; the list is loading until
  // both have settled.
  const listPending = Boolean(activeListReq.isPending) || Boolean(pendingListReq.isPending);

  // ---- fetch on mount + when country changes ----
  useEffect(() => {
    dispatch(cpGetVerticals());
    dispatch(cpGetPersonas());
    if (!contentConfigAudiencesEnabled) dispatch(cpListAttributes());
    dispatch(cpGetCountries()); // needed for per-country language tabs + country selector
    dispatch(cpListCvps('')); // CVPs are global — fetch the full list, not per-country
  }, [contentConfigAudiencesEnabled, dispatch]);
  useEffect(() => {
    if (
      contentConfigAudiencesEnabled
      && !productOfferingAudiencesEnabled
      && country
    ) {
      dispatch(cpListAudiences(country));
    }
  }, [
    contentConfigAudiencesEnabled,
    country,
    dispatch,
    productOfferingAudiencesEnabled,
  ]);
  const refetchAudiences = useCallback(() => {
    if (!productOfferingAudiencesEnabled) return;
    dispatch(cpListAudiences());
  }, [dispatch, productOfferingAudiencesEnabled]);
  useEffect(() => {
    refetchAudiences();
  }, [refetchAudiences]);
  const refetchProducts = useCallback(() => {
    if (!country) return;
    dispatch(cpListProducts(country));
    dispatch(cpListProducts(country, STATUS_PENDING));
  }, [country, dispatch]);
  // Default the selected country to the deep-linked one when valid, else the
  // first one (countries are sorted by name).
  useEffect(() => {
    if (!country && countries.length) {
      const linked = deepLink.country && countries.some((c) => c.id === deepLink.country)
        ? deepLink.country
        : '';
      setCountry(linked || countries[0].id);
    }
  }, [countries, country, deepLink.country]);
  useEffect(() => {
    refetchProducts();
  }, [refetchProducts]);

  // ---- merge active + pending rows by logical key ----
  // In-memory draft for a brand-new offering (country + vertical + name from the
  // modal). It is authored in the detail view and only persisted (via
  // CreateProduct) on "Send for approval"; not stored on the backend until then.
  const [draft, setDraft] = useState(null);

  const merged = useMemo(() => {
    const m = {};
    activeRaw.map(productFromApi).forEach((p) => {
      m[p.key] = { ...(m[p.key] || {}), key: p.key, vertical: p.vertical, active: p };
    });
    pendingRaw.map(productFromApi).forEach((p) => {
      m[p.key] = { ...(m[p.key] || {}), key: p.key, vertical: p.vertical, pending: p };
    });
    if (draft) m[DRAFT_KEY] = { key: DRAFT_KEY, vertical: draft.vertical, draft };
    return m;
  }, [activeRaw, pendingRaw, draft]);

  // Languages come straight from the selected country's API config — no fallback.
  const langs = useMemo(() => {
    const c = countries.find((x) => x.id === country);
    return (c && c.langs) ? c.langs : [];
  }, [countries, country]);

  // selectedKey === '' → list (table) view; otherwise the detail view for that key.
  const [selectedKey, setSelectedKey] = useState('');
  const [activeLang, setActiveLang] = useState('');
  const [edits, setEdits] = useState(null); // local sandbox for the selected product
  const [showNew, setShowNew] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [showGenerationSetup, setShowGenerationSetup] = useState(false);
  const [showGeneration, setShowGeneration] = useState(false);
  const [generationRun, setGenerationRun] = useState(EMPTY_GENERATION_RUN);
  const [generationSetupInitialAttributes, setGenerationSetupInitialAttributes] = useState(EMPTY_STRING_ARRAY);
  const [isRegeneration, setIsRegeneration] = useState(false);
  const [listTab, setListTab] = useState('all'); // 'all' | 'pending' | 'ci-enabled'
  const [verticalFilter, setVerticalFilter] = useState(''); // '' = all verticals
  // When a pending change exists, the editor shows the active (published)
  // version by default; "See changes" flips it to the pending version.
  const [showPending, setShowPending] = useState(false);

  // Clicking the "Product Offerings" nav item (even while already on it) returns
  // to the list — there's no in-page back button.
  useEffect(() => { setSelectedKey(''); setEdits(null); }, [navTick]);
  // If the open offering no longer exists for this country (recall/reject, or a
  // stale selection after a refetch), drop back to the list.
  useEffect(() => {
    if (selectedKey && selectedKey !== DRAFT_KEY && !merged[selectedKey] && !listPending) {
      setSelectedKey('');
    }
  }, [listPending, merged, selectedKey]);
  // Open the deep-linked config once it shows up in the merged rows. Waiting
  // for the key (rather than gating on listPending) avoids racing the
  // stale-selection effect above before the fetch has started. Once a full
  // load cycle settles without the key, the link is invalid — drop it.
  const sawListLoad = useRef(false);
  useEffect(() => {
    if (!deepLink.key) return;
    if (merged[deepLink.key]) {
      setSelectedKey(deepLink.key);
      clearDeepLink();
      return;
    }
    if (listPending) sawListLoad.current = true;
    else if (sawListLoad.current) clearDeepLink();
  }, [clearDeepLink, deepLink.key, listPending, merged]);
  // Bumped on every history change so the share-param sync below re-evaluates
  // after external navigation: the ucd top tabs keep their panes mounted, so a
  // tab switch rewrites the URL (stripping our params) without changing any of
  // this page's state — returning to the tab must restore them.
  const [locTick, setLocTick] = useState(0);
  useEffect(() => {
    // listen() returns an unlisten function; returning it unsubscribes on unmount.
    const unlisten = browserHistory.listen(() => setLocTick((t) => t + 1));
    return unlisten;
  }, []);
  // Keep the address bar shareable: an open (non-draft) config is reflected as
  // ?country=..&key=.. so the URL can be copied directly from the browser. Both
  // params are stripped on the list view so they don't follow the user to other
  // sections (deep links survive — they're captured into state on mount above).
  // replace (not push) — detail open/close is component state, not routing.
  useEffect(() => {
    if (deepLink.key) return; // don't strip params before the deep link is consumed
    const params = new URLSearchParams(window.location.search);
    // Only own the URL while content-config is the active ucd tab — this page
    // stays mounted (hidden) under other tabs, whose URLs must stay clean.
    if (params.get('tab') !== 'content-config') return;
    const before = params.toString();
    if (country && selectedKey && selectedKey !== DRAFT_KEY) {
      params.set('country', country);
      params.set('key', selectedKey);
    } else {
      params.delete('key');
      params.delete('country');
    }
    const after = params.toString();
    if (after !== before) browserHistory.replace(`${window.location.pathname}?${after}`);
  }, [country, deepLink.key, locTick, selectedKey]);
  // Strip the share params when this page unmounts (e.g. switching to another
  // content-config section) — they describe a view that is no longer on screen
  // and would otherwise linger. Scoped to the content-config tab URL: on route
  // transitions window.location is already the destination, whose country/key
  // params (if any) belong to that route, not to this page.
  useEffect(() => () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== 'content-config') return;
    if (!params.has('key') && !params.has('country')) return;
    params.delete('key');
    params.delete('country');
    const qs = params.toString();
    browserHistory.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, []);
  useEffect(() => {
    if (!langs.length) {
      if (activeLang) setActiveLang('');
      return;
    }
    if (!langs.includes(activeLang)) setActiveLang(langs[0]);
  }, [activeLang, langs]);
  useEffect(() => { setEdits(null); setShowDiff(false); setShowPending(false); }, [selectedKey, country]);

  const sel = merged[selectedKey];
  const isDraft = Boolean(sel && sel.draft && !sel.active && !sel.pending);
  // Displayed/edited record — active (published) by default; the pending version
  // when "See changes" is toggled on.
  const base = sel && (showPending && sel.pending ? sel.pending : (sel.active || sel.pending || sel.draft));
  const defaultLang = langs[0];
  const hasPending = Boolean(sel && sel.pending);
  const pendingAuthor = hasPending ? (sel.pending.updatedBy || sel.pending.createdBy) : null;
  const pendingExpiry = hasPending ? formatExpiry(sel.pending.expiresAt) : '';
  const isMine = pendingAuthor === userEmail;

  const onWriteSuccess = useCallback(({ toList } = {}) => {
    setEdits(null);
    setDraft(null);
    if (toList) setSelectedKey('');
    refetchProducts();
  }, [refetchProducts]);

  const { busy, runWrite } = useContentConfigWrite({ onSuccess: onWriteSuccess });
  const notifyForbidden = () => message.error(FORBIDDEN_MESSAGE);

  // ---- value resolution (sandbox over base) ----
  const langVal = (lang, field) => {
    const e = edits && edits.byLang && edits.byLang[lang];
    if (e && e[field] !== undefined) return e[field];
    const b = base && base.byLang && base.byLang[lang];
    return b && b[field] !== undefined ? b[field] : '';
  };
  const baseLangVal = (lang, field) => {
    const b = base && base.byLang && base.byLang[lang];
    return b && b[field] !== undefined ? b[field] : '';
  };
  const baseNameVal = () => (base && base.name) || '';
  const baseResolvedNameVal = () => baseNameVal() || baseLangVal(defaultLang || 'en', 'name');
  const nameVal = () => {
    if (edits && edits.name !== undefined) return edits.name;
    return baseResolvedNameVal();
  };
  const listVal = (field) => {
    const e = edits && edits.whitelist;
    if (e && e[field] !== undefined) return e[field];
    return (base && base.whitelist && base.whitelist[field]) || [];
  };
  const setNameEdit = (value) => setEdits((prev) => ({ ...(prev || {}), name: value }));
  const setLang = (lang, field, value) => setEdits((prev) => ({
    ...(prev || {}),
    byLang: { ...((prev || {}).byLang || {}), [lang]: { ...(((prev || {}).byLang || {})[lang] || {}), [field]: value } },
  }));
  const setList = (field, value) => setEdits((prev) => ({
    ...(prev || {}), whitelist: { ...((prev || {}).whitelist || {}), [field]: value },
  }));
  // Vertical lives in the same edit sandbox — changing it via the dropdown
  // stages a change (a pending update for an active product, or just edits the
  // draft) that "Send for approval" submits.
  const verticalVal = () => (edits && edits.vertical) || (base && base.vertical) || (verticals[0] && verticals[0].id);
  const setVerticalEdit = (v) => setEdits((prev) => ({ ...(prev || {}), vertical: v }));

  // ---- dirty check ----
  // Compare resolved (sandbox-over-base) values against the base record rather
  // than just "has the sandbox been touched". Restoring a field to its original
  // value clears the dirty state, so an unchanged update can't be submitted.
  const baseListVal = (field) => (base && base.whitelist && base.whitelist[field]) || [];
  const baseVerticalVal = () => (base && base.vertical) || (verticals[0] && verticals[0].id);
  // Link lists are compared as sets — reordering selections is not a change.
  const sameSet = (a, b) => a.length === b.length
    && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');
  const countryName = useCallback(
    (cid) => (countries.find((c) => c.id === cid) || {}).name || cid,
    [countries],
  );
  const audienceLabelCopy = useMemo(() => ({
    regional: i18n.t('ucd_content_config.product_offerings.audiences.regional'),
    segment: i18n.t('ucd_content_config.product_offerings.audiences.segment'),
    table: i18n.t('ucd_content_config.product_offerings.audiences.table'),
    unavailablePrefix: i18n.t(
      'ucd_content_config.product_offerings.audiences.unavailable_prefix',
    ),
    unknownSource: i18n.t('ucd_content_config.product_offerings.audiences.unknown_source'),
  }), []);
  const selectedAudienceAssociations = listVal('audiences');
  const audienceOptions = useMemo(
    () => buildProductAudienceOptions(
      audienceReferences,
      selectedAudienceAssociations,
      country,
      countryName,
      audienceLabelCopy,
    ),
    [
      audienceLabelCopy,
      audienceReferences,
      country,
      countryName,
      selectedAudienceAssociations,
    ],
  );
  const audienceOptionByKey = useMemo(
    () => new Map(audienceOptions.map((option) => [option.key, option])),
    [audienceOptions],
  );
  const audienceLabelForKey = useCallback((key) => {
    const option = audienceOptionByKey.get(key);
    if (option) return option.label;
    const reference = audienceReferences.find((audience) => audience.key === key);
    return reference
      ? audienceDisplayLabel(reference, countryName, audienceLabelCopy)
      : `${audienceLabelCopy.unavailablePrefix} · ${key}`;
  }, [audienceLabelCopy, audienceOptionByKey, audienceReferences, countryName]);
  const {
    audienceValidationUnavailable,
    hasInvalidAudiences,
  } = getProductAudienceValidationState(
    productOfferingAudiencesEnabled,
    Boolean(audienceListReq.isSuccessful),
    selectedAudienceAssociations,
    audienceOptionByKey,
  );
  const hasInvalidAudienceReasons = productOfferingAudiencesEnabled
    && selectedAudienceAssociations.some(
      ({ reasonToUse }) => !isAudienceReasonValid(reasonToUse),
    );
  const productNameReady = Boolean(nameVal().trim());
  const hasEdits = Boolean(edits) && (
    nameVal() !== baseResolvedNameVal()
    || verticalVal() !== baseVerticalVal()
    || langs.some((lang) => ['name', 'ciEnabled', 'overview', 'reason', 'cta']
      .some((field) => langVal(lang, field) !== baseLangVal(lang, field)))
    || ['cvps', 'attrs', 'personas'].some((field) => !sameSet(listVal(field), baseListVal(field)))
    || !sameAudienceAssociations(listVal('audiences'), baseListVal('audiences'))
  );

  // Build a full FE product (base merged with edits) for submission.
  const buildFE = () => {
    const byLang = {};
    langs.forEach((lang) => {
      const name = langVal(lang, 'name');
      const overview = langVal(lang, 'overview');
      const entry = {
        name,
        ciEnabled: Boolean(langVal(lang, 'ciEnabled')) && Boolean(name.trim() && overview.trim()),
        overview,
        reason: langVal(lang, 'reason'),
        cta: langVal(lang, 'cta'),
      };
      // Only include a language once it has a name — the backend requires a
      // name per provided language, so empty/untouched tabs are skipped.
      if (entry.name.trim()) byLang[lang] = entry;
    });
    return {
      name: nameVal().trim(),
      vertical: verticalVal(),
      byLang,
      whitelist: {
        cvps: listVal('cvps'),
        attrs: listVal('attrs'),
        personas: listVal('personas'),
        audiences: listVal('audiences'),
      },
    };
  };

  // Draft is ready to submit once the default language has a name + overview.
  const draftReady = isDraft
    && productNameReady
    && Boolean(langVal(defaultLang, 'name').trim() && langVal(defaultLang, 'overview').trim());
  const productSubmitState = {
    isDraft,
    draftReady,
    hasActive: Boolean(sel && sel.active),
    hasEdits,
    hasPending,
    hasInvalidAudiences,
    hasInvalidAudienceReasons,
  };

  const openDetail = (key) => { setSelectedKey(key); setEdits(null); clearDeepLink(); };
  const onCountryChange = (c) => {
    setCountry(c); setSelectedKey(''); setEdits(null); setDraft(null); clearDeepLink();
  };
  const discardDraft = () => { setDraft(null); setEdits(null); setSelectedKey(''); };

  const onSendForApproval = () => {
    if (isDraft) {
      if (!canCreate) {
        notifyForbidden();
        return;
      }
      if (!productNameReady) {
        message.error('Smart Comms tagged name is required.');
        return;
      }
      if (!canSubmitProductForApproval(contentConfigPermissions, productSubmitState)) return;
      runWrite(cpCreateProduct(country, productToApiBody(buildFE()), userEmail), 'createProduct', { toList: true });
      return;
    }
    if (!sel || !sel.active) return;
    if (!canUpdate) {
      notifyForbidden();
      return;
    }
    if (!productNameReady) {
      message.error('Smart Comms tagged name is required.');
      return;
    }
    if (!canSubmitProductForApproval(contentConfigPermissions, productSubmitState)) return;
    runWrite(cpUpdateProduct(sel.active.id, productToApiBody(buildFE()), userEmail), 'updateProduct');
  };
  const onApprove = () => {
    if (!sel || !sel.pending) return;
    if (!canReviewPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    runWrite(cpApprove(sel.pending.id, userEmail), 'approve');
  };
  const onRecall = () => {
    if (!sel || !sel.pending) return;
    if (!canRecallPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    runWrite(cpRecall(sel.pending.id, userEmail), 'recall');
  };
  const onReject = () => {
    if (!sel || !sel.pending) return;
    if (!canReviewPendingChange(contentConfigPermissions, isMine)) {
      notifyForbidden();
      return;
    }
    const pendingId = sel.pending.id;
    let comment = '';
    Modal.confirm({
      title: 'Reject pending change?',
      content: (<Input.TextArea placeholder="Reason (required)" onChange={(e) => { comment = e.target.value; }} />),
      okText: 'Reject',
      okType: 'danger',
      onOk: () => {
        if (!canReviewPendingChange(contentConfigPermissions, isMine)) { notifyForbidden(); return Promise.reject(); }
        if (!comment.trim()) { message.error('A rejection comment is required.'); return Promise.reject(); }
        runWrite(cpReject(pendingId, userEmail, comment), 'reject');
        return Promise.resolve();
      },
    });
  };
  const verticalName = (vid) => (verticals.find((v) => v.id === vid) || {}).name || vid;

  // Content generation always uses the published version. This is deliberately
  // separate from `base`, which can point at the pending version while the user
  // is reviewing changes.
  const generationBase = sel && sel.active;
  const generationAttributes = generationBase && generationBase.whitelist
    ? generationBase.whitelist.attrs || []
    : [];
  const generationMatchesCurrentContext = Boolean(
    generationRun.requests.length > 0
    && generationBase
    && generationRun.productUUID === generationBase.key
    && generationRun.context.country === country,
  );
  const generationHasFailures = generationRun.attributes.some((attribute) =>
    generationFailedAttributes.includes(attribute));
  let generationDisabledReason = '';
  if (hasPending && !generationBase) {
    generationDisabledReason = 'Content generation is available after this new product offering is approved.';
  } else if (!generationBase || isDraft || !generationBase.key) {
    generationDisabledReason = 'Save this product offering before generating content.';
  } else if (langs.length === 0) {
    generationDisabledReason = 'No languages are configured for this country.';
  } else if (generationAttributes.length === 0) {
    generationDisabledReason = 'Link at least one attribute before generating content.';
  } else if (generationRequest.isPending && !generationMatchesCurrentContext) {
    generationDisabledReason = 'Content generation is already in progress for another product offering.';
  }

  const openGenerationSetup = (initialAttributes = generationAttributes, regenerate = false) => {
    if (generationDisabledReason) {
      message.error(generationDisabledReason);
      return;
    }
    setGenerationSetupInitialAttributes(initialAttributes);
    setIsRegeneration(regenerate);
    setShowGenerationSetup(true);
  };

  const openGeneration = () => {
    if (generationDisabledReason) {
      message.error(generationDisabledReason);
      return;
    }
    if (generationMatchesCurrentContext && (
      generationRequest.isPending
      || generationRequest.isSuccessful
      || generationRequest.isFailure
    )) {
      setShowGeneration(true);
      return;
    }
    openGenerationSetup(generationAttributes, false);
  };

  const onGenerateContent = (selectedAttributes, dateRange, selectedLanguage) => {
    if (generationRequest.isPending) return;
    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');
    const productName = productDisplayName(generationBase, selectedLanguage);
    const requests = buildContentConfigGenerationRequests({
      attributes: selectedAttributes,
      country,
      endDate,
      language: selectedLanguage,
      productUUID: generationBase.key,
      startDate,
      vertical: generationBase.vertical,
    });
    const attributeDefinitions = {};
    attrOptions.forEach((attribute) => {
      attributeDefinitions[attribute.key] = attribute.definition || '';
    });

    setGenerationRun({
      attributes: selectedAttributes,
      attributeDefinitions,
      context: {
        country,
        endDate,
        language: selectedLanguage,
        product: productName,
        startDate,
        vertical: generationBase.vertical,
      },
      productUUID: generationBase.key,
      requests,
    });
    setShowGenerationSetup(false);
    setShowGeneration(true);
    dispatch(contentConfigGenerateMystiqueV2(requests));
  };

  const retryContentGeneration = () => {
    if (generationRequest.isPending) return;
    const missingRequests = selectMissingContentConfigGenerationRequests(
      generationRun.requests,
      generationReadyAttributes,
    );
    if (missingRequests.length === 0) return;
    dispatch(contentConfigGenerateMystiqueV2(missingRequests, { preserveExisting: true }));
  };

  const regenerateSingleAttribute = (attribute) => {
    if (generationRequest.isPending) return;
    const request = generationRun.requests.find((item) => item.attribute === attribute);
    if (!request) return;
    dispatch(contentConfigGenerateMystiqueV2([request], { preserveExisting: true }));
  };

  const cancelGenerationSetup = () => {
    setShowGenerationSetup(false);
  };


  // ---- list (table) view ----
  const rowStatus = (m) => {
    if (m.pending) return 'pending';
    if (m.active) return 'active';
    return 'draft';
  };
  const rows = useMemo(() => Object.keys(merged).map((k) => {
    const m = merged[k];
    const rec = m.active || m.pending || m.draft;
    const ciEnabledLangs = langs.filter((lang) => {
      const byLang = rec && rec.byLang && rec.byLang[lang];
      return Boolean(byLang && byLang.ciEnabled && byLang.name && byLang.name.trim() && byLang.overview && byLang.overview.trim());
    });
    return {
      key: k,
      name: productDisplayName(rec, defaultLang || 'en'),
      vertical: m.vertical,
      status: rowStatus(m),
      ciEnabledLangs,
      updatedBy: rec.updatedBy || rec.createdBy || '',
      updatedAt: rec.updatedAt,
    };
  }), [merged, defaultLang, langs]); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleRows = useMemo(() => {
    let r = rows;
    if (verticalFilter) r = r.filter((x) => x.vertical === verticalFilter);
    if (listTab === 'pending') r = r.filter((x) => x.status === 'pending');
    if (listTab === 'ci-enabled') r = r.filter((x) => x.ciEnabledLangs.length > 0);
    return r;
  }, [listTab, rows, verticalFilter]);

  return (
    <div className="cc-offerings-page">
      {selectedKey ? (
        <ProductOfferingDetail
          activeLang={activeLang}
          attrOptions={attrOptions}
          audienceLabelForKey={audienceLabelForKey}
          audienceListFailed={Boolean(audienceListReq.isFailure)}
          audienceListLoaded={Boolean(audienceListReq.isSuccessful)}
          audienceListLoading={Boolean(audienceListReq.isPending)}
          audienceOptions={audienceOptions}
          audienceValidationUnavailable={audienceValidationUnavailable}
          audiencesEnabled={productOfferingAudiencesEnabled}
          base={base}
          busy={busy}
          canCreate={canCreate}
          canReview={canReview}
          canUpdate={canUpdate}
          country={country}
          countryName={countryName}
          cvpOptions={cvpOptions}
          defaultLang={defaultLang}
          discardDraft={discardDraft}
          discardLocalEdits={() => setEdits(null)}
          draftReady={draftReady}
          hasEdits={hasEdits}
          hasInvalidAudienceReasons={hasInvalidAudienceReasons}
          hasInvalidAudiences={hasInvalidAudiences}
          hasPending={hasPending}
          isDraft={isDraft}
          isMine={isMine}
          generationDisabledReason={generationDisabledReason}
          langVal={langVal}
          langs={langs}
          listVal={listVal}
          nameVal={nameVal}
          onApprove={onApprove}
          onGenerate={openGeneration}
          onRecall={onRecall}
          onReject={onReject}
          onRetryAudiences={refetchAudiences}
          onSendForApproval={onSendForApproval}
          pendingAuthor={pendingAuthor}
          pendingExpiry={pendingExpiry}
          personaOptions={personaOptions}
          productNameReady={productNameReady}
          sel={sel}
          setActiveLang={setActiveLang}
          setLang={setLang}
          setList={setList}
          setNameEdit={setNameEdit}
          setShowDiff={setShowDiff}
          setShowPending={setShowPending}
          setVerticalEdit={setVerticalEdit}
          showDiff={showDiff}
          showPending={showPending}
          verticalName={verticalName}
          verticals={verticals}
          verticalVal={verticalVal}
        />
      ) : (
        <ProductOfferingList
          country={country}
          countries={countries}
          canCreate={canCreate}
          listTab={listTab}
          setListTab={setListTab}
          loading={listPending && rows.length === 0}
          onCountryChange={onCountryChange}
          onCreate={() => {
            if (!canCreate) {
              notifyForbidden();
              return;
            }
            setShowNew(true);
          }}
          onOpenDetail={openDetail}
          rows={visibleRows}
          setVerticalFilter={setVerticalFilter}
          verticalFilter={verticalFilter}
          verticalName={verticalName}
          verticals={verticals}
        />
      )}

      <NewProductModal
        open={showNew}
        canCreate={canCreate}
        countries={countries}
        defaultCountry={country}
        verticals={verticals}
        onClose={() => setShowNew(false)}
        onCreate={(stub) => {
          if (!canCreate) {
            notifyForbidden();
            return;
          }
          // Start an in-memory draft for the chosen country and open the detail
          // view to author it; it is only persisted (CreateProduct) on "Send for
          // approval".
          setCountry(stub.country);
          setDraft(stub);
          setEdits(null);
          setActiveLang('en');
          setSelectedKey(DRAFT_KEY);
          setShowNew(false);
        }}
      />

      <ContentGenerationModal
        attributes={generationRun.attributes}
        attributeDefinitions={generationRun.attributeDefinitions}
        context={generationRun.context}
        isFailure={generationHasFailures && !generationRequest.isPending}
        isLoading={Boolean(generationRequest.isPending)}
        onClose={() => setShowGeneration(false)}
        onRegenerate={(attributes) => openGenerationSetup(
          attributes && attributes.length > 0 ? attributes : generationRun.attributes,
          true,
        )}
        onRegenerateAttribute={regenerateSingleAttribute}
        onRetry={retryContentGeneration}
        open={showGeneration}
        readyAttributes={generationReadyAttributes}
        responsesByAttribute={generationResponses}
        usesActiveConfig={hasPending && Boolean(generationBase)}
      />

      <ContentGenerationSetupModal
        attributes={generationAttributes}
        initialAttributes={generationSetupInitialAttributes}
        initialLanguage={isRegeneration ? generationRun.context.language : 'en'}
        isRegeneration={isRegeneration}
        languages={langs}
        onCancel={cancelGenerationSetup}
        onGenerate={onGenerateContent}
        open={showGenerationSetup}
      />
    </div>
  );
};

export default ProductOfferingPage;
